import { Box, Button, Stack, Typography } from '@mui/material';
import CloseIcon from 'assets/img/icons/CloseIcon';
import { useCheckClaim } from 'pages/PlaygroundV2Page/hooks/useCheckClaim';
import { useGenerate } from 'pages/PlaygroundV2Page/hooks/useGenerate';
import SourceLabel from 'pages/PlaygroundV2Page/NoSourcesFoundComponent/components/SourceLabel';
import { BORDER_COLOR, DARKER_GREY, YELLOW } from 'utils/theme';
import { getClaimKeys } from '../../helper';
import ClaimActions from './ClaimActions/ClaimActions';

interface ClaimSummaryProps {
  claim: Claim;
  claimIndex: number;
  setExpanded: (value: string | null) => void;
  expandedClaim: string | null;
}

const ClaimSummary = ({ claim, claimIndex, setExpanded, expandedClaim }: ClaimSummaryProps) => {
  const checkClaim = useCheckClaim();
  const getGeneratedClaims = useGenerate();

  const label =
    claim.finalPrediction === 1
      ? 'supported'
      : claim.finalPrediction === 0
      ? 'disputed'
      : claim.hasBeenChecked
      ? 'unverified'
      : null;

  const { summaryKey, rephraseKey } = getClaimKeys(claim.claim);
  const isSummaryOpen = expandedClaim === summaryKey;
  const isSourceOpen = expandedClaim === claim.claim;
  const isRephraseOpen = expandedClaim === rephraseKey;

  const showCheckClaimButton =
    !claim?.evidence?.length && !claim.isBeingChecked && !claim.hasBeenChecked;

  const showLabel =
    !isSourceOpen && !isSummaryOpen && !isRephraseOpen && !claim.isBeingChecked && label;

  const showCloseButton = isSourceOpen || isRephraseOpen || isSummaryOpen;

  return (
    <Box
      sx={{
        background: 'white',
        border: `1px solid ${BORDER_COLOR}`,
        px: { mobile: '16px', tablet: '30px', desktop: '30px' },
        paddingTop: { mobile: 2, tablet: 2, desktop: 3 },
        borderTopLeftRadius: '12px',
        borderTopRightRadius: '12px',
        borderBottomLeftRadius: expandedClaim === claim.claim ? 0 : '12px',
        borderBottomRightRadius: expandedClaim === claim.claim ? 0 : '12px',
        position: 'sticky',
        top: 0,
        zIndex: 1,
      }}
    >
      <Stack
        direction={{ mobile: 'column', tablet: 'row' }}
        alignItems={{ mobile: 'flex-start', tablet: 'baseline' }}
        justifyContent="space-between"
        borderBottom={`1px solid ${BORDER_COLOR}`}
      >
        <Stack direction="row" alignItems="baseline" pb={2}>
          <Typography fontSize="16px" mr={1}>
            {claimIndex + 1}.
          </Typography>
          <Typography fontSize="18px">{claim.claim}</Typography>
        </Stack>
        <Box
          sx={{
            width: { mobile: '100%', tablet: '120px' },
            minWidth: { mobile: 'auto', tablet: '120px' },
            display: 'flex',
            justifyContent: { mobile: 'flex-start', tablet: 'flex-end' },
            ml: { mobile: 0, tablet: 3 },
            mb: { mobile: 2, tablet: 0 },
          }}
        >
          {showLabel && <SourceLabel label={label} />}
          {showCloseButton && (
            <Button
              onClick={() => setExpanded(null)}
              endIcon={<CloseIcon />}
              sx={{
                textTransform: 'capitalize',
                color: '#545454B2',
                backgroundColor: '#F1F2FB',
                borderRadius: '4px',
              }}
            >
              Close
            </Button>
          )}
          {showCheckClaimButton && (
            <Button
              sx={{
                border: `1.5px solid ${YELLOW}`,
                borderRadius: '32px',
                width: '120px',
                textTransform: 'none',
                padding: '6px 8px',
                boxSizing: 'border-box',
                minWidth: '120px',
              }}
              onClick={() => {
                checkClaim(claim.claim);
                getGeneratedClaims(claim.claim);
              }}
            >
              <Typography color={DARKER_GREY}>Analyze</Typography>
            </Button>
          )}
        </Box>
      </Stack>

      <ClaimActions claim={claim} expandedClaim={expandedClaim} setExpanded={setExpanded} />
    </Box>
  );
};

export default ClaimSummary;
